import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, Circle, ChevronDown, ChevronRight, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

function ProgressBar({ value }) {
  return (
    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full bg-indigo-600 rounded-full transition-all duration-500"
        style={{ width: `${value}%` }} />
    </div>
  );
}

export default function ProjectCDCPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState({});
  const [dates, setDates] = useState({});
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () =>
    axios.get(`${API_URL}/api/projects/${projectId}/cdc`, auth)
      .then(({ data }) => { setData(data); setDraft(data.cdc); setDates(data.dates || {}); })
      .catch(() => toast.error("Impossible de charger le CDC"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const toggleSpec = async (text, checked) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/projects/${projectId}/cdc/toggle`,
        { spec_text: text, checked }, auth);
      setData(data); setDraft(data.cdc);
    } catch (e) { toast.error("Erreur de mise à jour"); }
  };

  const setSpecDate = async (key, field, value) => {
    const cur = dates[key] || { debut: "", fin: "" };
    const next = { ...cur, [field]: value };
    setDates((d) => ({ ...d, [key]: next }));
    try {
      await axios.put(`${API_URL}/api/projects/${projectId}/cdc/dates`,
        { key, debut: next.debut, fin: next.fin }, auth);
    } catch (e) { toast.error("Erreur d'enregistrement de la date"); }
  };

  const saveCdc = async () => {
    try {
      const { data } = await axios.put(`${API_URL}/api/projects/${projectId}/cdc`, { cdc: draft }, auth);
      setData(data); setEditMode(false);
      toast.success("CDC enregistré");
    } catch (e) { toast.error("Erreur d'enregistrement"); }
  };

  const Tabs = () => (
    <ProjectTabs projectId={projectId} actif="cdc" />
  );

  if (loading) {
    return <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div></CRMLayout>;
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const maquettes = [...String(data.cdc || "").matchAll(/!\[([^\]]*)\]\((https:\/\/novaforge\.pro\/cdc-assets\/[^)]+)\)/g)]
    .map((m) => ({ legende: m[1], url: m[2] }));
  const Spec = ({ s }) => {
    const d = dates[s.text] || {};
    const overdue = d.fin && d.fin < todayStr && !s.checked;
    const soon = d.fin && !overdue && !s.checked && (new Date(d.fin) - new Date()) / 86400000 <= 7;
    return (
      <div className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-slate-50 group">
        <button onClick={() => toggleSpec(s.text, !s.checked)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
          {s.checked
            ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            : <Circle className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 shrink-0" />}
          <span className={`text-sm ${s.checked ? "text-slate-400 line-through" : "text-slate-700"}`}>{s.text}</span>
          {overdue && <span className="text-[10px] font-semibold text-red-600 bg-red-50 rounded px-1.5 py-0.5 shrink-0">en retard</span>}
          {soon && <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 rounded px-1.5 py-0.5 shrink-0">bientôt</span>}
        </button>
        <div className="flex items-center gap-1 shrink-0" title="Début → Fin">
          <input type="date" value={d.debut || ""} onChange={(e) => setSpecDate(s.text, "debut", e.target.value)}
            className="text-[11px] border border-slate-200 rounded px-1 py-0.5 text-slate-500 bg-white w-[112px]" />
          <span className="text-slate-300 text-xs">→</span>
          <input type="date" value={d.fin || ""} onChange={(e) => setSpecDate(s.text, "fin", e.target.value)}
            className={`text-[11px] border rounded px-1 py-0.5 bg-white w-[112px] ${overdue ? "border-red-300 text-red-600" : "border-slate-200 text-slate-500"}`} />
        </div>
      </div>
    );
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-4xl">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <Tabs />

        {/* Progression globale */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-5">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2 className="font-bold text-slate-900">Progression globale</h2>
              <p className="text-xs text-slate-500">{data.done} / {data.total} spécifications livrées</p>
            </div>
            <div className="text-3xl font-bold text-indigo-600 tabular-nums">{data.progress}%</div>
          </div>
          <ProgressBar value={data.progress} />
          <div className="flex justify-end mt-3">
            <Button variant="outline" size="sm" onClick={() => setEditMode((v) => !v)}>
              {editMode ? "Annuler l'édition" : "Éditer le CDC"}
            </Button>
          </div>
        </div>

        {editMode ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <p className="text-xs text-slate-500 mb-2">Format : <code>##</code> lot · <code>###</code> section · <code>- [ ]</code> spec</p>
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
              className="w-full font-mono text-xs border border-slate-200 rounded-lg p-3 min-h-[420px]" />
            <div className="flex justify-end mt-3">
              <Button onClick={saveCdc} className="bg-indigo-600 hover:bg-indigo-700">
                <Save className="w-4 h-4 mr-1.5" /> Enregistrer
              </Button>
            </div>
          </div>
        ) : data.lots.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
            Aucun CDC pour ce projet.<br />
            <span className="text-xs">Cliquez sur « Éditer le CDC » pour en rédiger un.</span>
          </div>
        ) : (
          <div className="space-y-3">
            {data.lots.map((lot, i) => {
              const isOpen = open[i] !== false;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                  <button onClick={() => setOpen({ ...open, [i]: !isOpen })}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-slate-50">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <span className="font-semibold text-slate-900 text-sm text-left flex-1">{lot.title}</span>
                    <span className="text-xs text-slate-400 tabular-nums">{lot.done}/{lot.total}</span>
                    <div className="w-24"><ProgressBar value={lot.progress} /></div>
                    <span className="text-xs font-bold text-indigo-600 tabular-nums w-10 text-right">{lot.progress}%</span>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-4 pt-1 border-t border-slate-100">
                      {lot.specs.map((s, k) => <Spec key={`d${k}`} s={s} />)}
                      {lot.sections.map((sec, j) => (
                        <div key={j} className="mt-3">
                          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 px-2 mb-1">{sec.title}</div>
                          {sec.specs.map((s, k) => <Spec key={`${j}-${k}`} s={s} />)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {maquettes.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">Maquettes fonctionnelles</div>
                  <p className="text-xs text-slate-500 mt-1">Captures illustratives de l’expérience proposée, à valider avec la cliente.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
                  {maquettes.map((image, index) => (
                    <figure key={index} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                      <img src={image.url} alt={image.legende || "Maquette Keepcool"} loading="lazy" className="w-full h-auto" />
                      <figcaption className="px-3 py-2 text-xs text-slate-500 bg-white border-t border-slate-100">{image.legende}</figcaption>
                    </figure>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
